import { useEffect, useState, useMemo, useCallback } from "react";
import { api, downloadFile, formatApiError, queuedMessage } from "@/lib/api";
import Layout from "@/components/Layout";
import ConfirmDelete from "@/components/ConfirmDelete";
import { Plus, Download, Edit3, X, Zap, Settings } from "lucide-react";
import { toast, Toaster } from "sonner";
import { AreaChart, Area, BarChart, Bar, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from "recharts";
import { useAuth } from "@/context/AuthContext";

const todayStr = () => new Date().toISOString().slice(0, 10);

export default function Power() {
  const { user, isSuper, canSeePrices } = useAuth();
  const isAdmin = isSuper || user?.role === "admin" || canSeePrices;
  const isOp = !isAdmin;
  const [items, setItems] = useState([]);
  const [view, setView] = useState("daily");
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [remark, setRemark] = useState("");
  const [showSettings, setShowSettings] = useState(false);
  const [settings, setSettings] = useState({ meter_name: "Main", rate_per_unit: 0 });
  const [form, setForm] = useState({ date: todayStr(), meter_name: "Main", opening_reading: 0, closing_reading: 0, rate_per_unit: 0, remarks: "" });

  const load = useCallback(() => api.get("/power").then((r) => setItems(r.data)), []);
  useEffect(() => { load(); api.get("/power/settings").then((r) => setSettings(r.data)); }, [load]);

  const openForm = async () => {
    setEditing(null); setRemark("");
    const { data } = await api.get("/power/next-opening", { params: { meter_name: settings.meter_name || "Main" } });
    setForm({ date: todayStr(), meter_name: data.meter_name, opening_reading: data.opening_reading, closing_reading: data.opening_reading, rate_per_unit: data.rate_per_unit, remarks: "" });
    setShowForm(true);
  };

  const startEdit = (p) => {
    setEditing(p); setRemark("");
    setForm({ date: p.date, meter_name: p.meter_name, opening_reading: p.opening_reading, closing_reading: p.closing_reading, rate_per_unit: p.rate_per_unit, remarks: p.remarks || "" });
    setShowForm(true);
  };

  const onSave = async () => {
    if (editing && isOp && !remark.trim()) { toast.error("A remark is required to request this change"); return; }
    try {
      const payload = { ...form, opening_reading: +form.opening_reading, closing_reading: +form.closing_reading, rate_per_unit: +form.rate_per_unit };
      const cfg = isOp && editing ? { params: { remark } } : {};
      let res;
      if (editing) res = await api.put(`/power/${editing.id}`, payload, cfg);
      else res = await api.post("/power", payload);
      const q = queuedMessage(res);
      toast[q ? "info" : "success"](q || (editing ? "Power entry updated" : "Power entry saved")); setShowForm(false); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const onDelete = async (id, rmk) => {
    try {
      const res = await api.delete(`/power/${id}`, isOp ? { params: { remark: rmk } } : {});
      const q = queuedMessage(res);
      toast[q ? "info" : "success"](q || "Deleted"); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const units = form.closing_reading - form.opening_reading;
  const cost = (units * (form.rate_per_unit || 0)).toFixed(2);

  const monthly = useMemo(() => {
    const map = {};
    for (const p of items) {
      const k = p.date.slice(0, 7);
      map[k] = map[k] || { period: k, units: 0, cost: 0 };
      map[k].units += p.units_consumed || 0;
      map[k].cost += p.cost || 0;
    }
    return Object.values(map).sort((a, b) => a.period.localeCompare(b.period));
  }, [items]);

  const daily = useMemo(() => items.slice(0, 30).reverse().map((p) => ({ period: p.date, units: p.units_consumed, cost: p.cost })), [items]);
  const tooltipStyle = useMemo(() => ({ background: "#0F172A", color: "#fff", border: "none", borderRadius: 8, fontSize: 12 }), []);
  const tickStyle = useMemo(() => ({ fontSize: 11 }), []);

  const totalUnits = items.reduce((a, p) => a + (p.units_consumed || 0), 0);
  const totalCost = items.reduce((a, p) => a + (p.cost || 0), 0);

  return (
    <Layout title="Power Consumption" subtitle="Meter readings → daily and monthly consumption"
      actions={
        <div className="flex gap-2">
          <button data-testid="power-export-btn" onClick={() => downloadFile("/export/power", "Power_Report.xlsx")} className="bg-white border border-slate-300 hover:border-blue-500 px-3 py-2 rounded-lg text-sm font-medium text-slate-700 flex items-center gap-2"><Download size={16} /> Export</button>
          <button data-testid="power-add-btn" onClick={openForm} className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-semibold flex items-center gap-2"><Plus size={16} /> Add Reading</button>
          {isAdmin && <button data-testid="power-settings-btn" onClick={() => setShowSettings(true)} className="bg-white border border-slate-300 hover:border-blue-500 px-3 py-2 rounded-lg text-sm font-medium text-slate-700 flex items-center gap-2"><Settings size={16} /> Settings</button>}
        </div>
      }
    >
      <Toaster richColors position="top-right" />

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <div className="kpi-card kpi-warning">
          <div className="text-xs uppercase font-semibold text-slate-500">Total Units</div>
          <div className="font-heading text-3xl font-bold text-slate-900 mt-2">{totalUnits.toLocaleString()} units</div>
        </div>
        {isAdmin && (
          <div className="kpi-card kpi-success">
            <div className="text-xs uppercase font-semibold text-slate-500">Total Cost</div>
            <div className="font-heading text-3xl font-bold text-slate-900 mt-2">₹ {totalCost.toLocaleString()}</div>
          </div>
        )}
        <div className="kpi-card kpi-accent">
          <div className="text-xs uppercase font-semibold text-slate-500">Entries</div>
          <div className="font-heading text-3xl font-bold text-slate-900 mt-2">{items.length}</div>
        </div>
      </div>

      <div className="section-card mb-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading text-lg font-semibold">Consumption Chart</h3>
          <div className="flex gap-2">
            {[["daily", "Daily"], ["monthly", "Monthly"]].map(([v, l]) => (
              <button key={v} data-testid={`view-${v}`} onClick={() => setView(v)} className={`px-3 py-1.5 text-sm rounded-lg ${view === v ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-700"}`}>{l}</button>
            ))}
          </div>
        </div>
        <div className="h-72">
          <ResponsiveContainer>
            {view === "daily" ? (
              <AreaChart data={daily}>
                <defs><linearGradient id="pwr2" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#F59E0B" stopOpacity={0.6} /><stop offset="100%" stopColor="#F59E0B" stopOpacity={0} /></linearGradient></defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="period" tick={tickStyle} /><YAxis tick={tickStyle} />
                <Tooltip contentStyle={tooltipStyle} />
                <Area type="monotone" dataKey="units" stroke="#F59E0B" fill="url(#pwr2)" strokeWidth={2.5} />
              </AreaChart>
            ) : (
              <BarChart data={monthly}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="period" tick={tickStyle} /><YAxis tick={tickStyle} />
                <Tooltip contentStyle={tooltipStyle} />
                <Legend />
                <Bar dataKey="units" fill="#F59E0B" radius={[6, 6, 0, 0]} />
                {isAdmin && <Bar dataKey="cost" fill="#2563EB" radius={[6, 6, 0, 0]} />}
              </BarChart>
            )}
          </ResponsiveContainer>
        </div>
      </div>

      <div className="section-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider">
            <tr>
              <th className="text-left py-3 px-4">Date</th>
              <th className="text-left py-3 px-4">Meter</th>
              <th className="text-right py-3 px-4">Opening</th>
              <th className="text-right py-3 px-4">Closing</th>
              <th className="text-right py-3 px-4">Units</th>
              {isAdmin && <th className="text-right py-3 px-4">Rate</th>}
              {isAdmin && <th className="text-right py-3 px-4">Cost</th>}
              <th></th>
            </tr>
          </thead>
          <tbody data-testid="power-table-body">
            {items.length === 0 && <tr><td colSpan="8" className="text-center py-10 text-slate-400">No readings yet.</td></tr>}
            {items.map((p) => (
              <tr key={p.id} className="border-t border-slate-100 hover:bg-slate-50/50">
                <td className="py-2.5 px-4 font-medium">{p.date}</td>
                <td className="py-2.5 px-4">{p.meter_name}</td>
                <td className="py-2.5 px-4 text-right font-mono">{p.opening_reading.toLocaleString()}</td>
                <td className="py-2.5 px-4 text-right font-mono">{p.closing_reading.toLocaleString()}</td>
                <td className="py-2.5 px-4 text-right font-mono font-bold text-amber-600">{p.units_consumed.toLocaleString()}</td>
                {isAdmin && <td className="py-2.5 px-4 text-right">₹ {p.rate_per_unit}</td>}
                {isAdmin && <td className="py-2.5 px-4 text-right font-semibold">₹ {p.cost?.toLocaleString()}</td>}
                <td className="py-2.5 px-4 text-right whitespace-nowrap">
                  <button data-testid={`edit-power-${p.id}`} onClick={() => startEdit(p)} className="text-slate-400 hover:text-blue-600 p-1"><Edit3 size={16} /></button>
                  <ConfirmDelete testId={`delete-power-${p.id}`} requireRemark={isOp} onConfirm={(rmk) => onDelete(p.id, rmk)} title="Delete power reading?" />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-slate-900/60 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xl">
            <div className="flex items-center justify-between p-5 border-b border-slate-200">
              <h2 className="font-heading text-xl font-semibold flex items-center gap-2"><Zap className="text-amber-500" size={20} /> {editing ? "Edit Power Reading" : "New Power Reading"}</h2>
              <button onClick={() => setShowForm(false)} className="text-slate-400 hover:text-slate-700"><X size={20} /></button>
            </div>
            <div className="p-5 grid grid-cols-2 gap-4">
              <Field label="Date"><input data-testid="power-date" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" /></Field>
              <Field label="Meter"><input data-testid="power-meter" value={form.meter_name} disabled className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm bg-slate-50 text-slate-600" /></Field>
              <Field label={editing ? "Opening Reading" : "Opening Reading (auto)"}><input data-testid="power-opening" type="number" value={form.opening_reading} disabled={!editing || !isAdmin} onChange={(e) => setForm({ ...form, opening_reading: e.target.value })} className={`w-full border rounded-lg px-3 py-2 text-sm ${editing && isAdmin ? "border-slate-300" : "border-slate-200 bg-slate-50 text-slate-600"}`} /></Field>
              <Field label="Closing Reading"><input data-testid="power-closing" type="number" value={form.closing_reading} onChange={(e) => setForm({ ...form, closing_reading: e.target.value })} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" /></Field>
              {isAdmin && <Field label="Rate per Unit (₹)"><input data-testid="power-rate" type="number" step="0.01" value={form.rate_per_unit} disabled={!editing} onChange={(e) => setForm({ ...form, rate_per_unit: e.target.value })} className={`w-full border rounded-lg px-3 py-2 text-sm ${editing ? "border-slate-300" : "border-slate-200 bg-slate-50 text-slate-600"}`} /></Field>}
              <Field label="Remarks"><input data-testid="power-remarks" value={form.remarks} onChange={(e) => setForm({ ...form, remarks: e.target.value })} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" /></Field>
              <div className="col-span-2 bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-center justify-between">
                <div className="text-sm text-amber-800">Units to be recorded:</div>
                <div className="font-heading font-bold text-amber-700">{Math.max(0, units)} units{isAdmin ? ` · ₹ ${cost}` : ""}</div>
              </div>
              {editing && isOp && (
                <div className="col-span-2"><label className="block text-sm font-medium text-amber-700 mb-1.5">Reason for edit (sent to admin for approval)</label><textarea data-testid="power-approval-remark" value={remark} onChange={(e) => setRemark(e.target.value)} rows={2} className="w-full border border-amber-300 rounded-lg px-3 py-2 text-sm bg-amber-50" /></div>
              )}
            </div>
            <div className="flex justify-end gap-2 p-5 border-t border-slate-200 bg-slate-50">
              <button onClick={() => setShowForm(false)} className="px-4 py-2 border border-slate-300 rounded-lg text-sm font-medium text-slate-700 bg-white">Cancel</button>
              <button data-testid="power-save-btn" onClick={onSave} className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-semibold">Save</button>
            </div>
          </div>
        </div>
      )}
      {showSettings && (
        <div className="fixed inset-0 bg-slate-900/60 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
            <div className="flex items-center justify-between p-5 border-b"><h2 className="font-heading text-xl font-semibold flex items-center gap-2"><Settings className="text-blue-600" size={20} /> Power Settings</h2><button onClick={() => setShowSettings(false)}><X /></button></div>
            <div className="p-5 space-y-4">
              <Field label="Meter Name"><input data-testid="set-meter" value={settings.meter_name} onChange={(e) => setSettings({ ...settings, meter_name: e.target.value })} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" /></Field>
              <Field label="Rate per Unit (₹)"><input data-testid="set-rate" type="number" step="0.01" value={settings.rate_per_unit} onChange={(e) => setSettings({ ...settings, rate_per_unit: e.target.value })} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" /></Field>
            </div>
            <div className="flex justify-end gap-2 p-5 border-t bg-slate-50"><button onClick={() => setShowSettings(false)} className="px-4 py-2 border rounded-lg text-sm font-medium bg-white">Cancel</button><button data-testid="set-save" onClick={async () => { try { await api.put("/power/settings", { meter_name: settings.meter_name, rate_per_unit: +settings.rate_per_unit }); toast.success("Saved"); setShowSettings(false); } catch (e) { toast.error(formatApiError(e)); } }} className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-semibold">Save</button></div>
          </div>
        </div>
      )}
    </Layout>
  );
}

function Field({ label, children }) { return <div><label className="block text-sm font-medium text-slate-700 mb-1.5">{label}</label>{children}</div>; }
